import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ConfirmModal from '../../components/common/ConfirmModal';
import ProofNoteForm from './ProofNoteForm';
import ProofRevisionModal from './ProofRevisionModal';
import PaymentPanel from '../../components/payment/PaymentPanel';
import { ProofNoteInput, ProofRevisionInput, REVISION_ITEM_LABEL, RevisionItem } from './proof';
import { useUserSession } from '../Auth/userSession';
import { fetchMyOrderDetail } from './myOrdersService';
import { getProofToken, saveProofToken, clearProofToken } from './proofAccess';
import { OrderStatus, ORDER_STATUS_LABEL, WorkStatus, WORK_STATUS_LABEL } from '../../types/order';
import {
  fetchProof,
  submitProofRevision,
  sendProofNote,
  approveProof,
  ProofFileInfo,
  ProofFeedbackRecord,
  ProofRevisionInfo,
  ProofData,
} from '../../services/proofService';
import { ApiError, apiUrl } from '../../services/apiClient';
import styles from './ProofViewer.module.scss';

// 작업 상태 배지 색
const WORK_BADGE: Record<WorkStatus, string> = {
  WAITING: styles.badgeWait,
  IN_PROGRESS: styles.badgeInfo,
  COMPLETED: styles.badgeDone,
};

// 최종 승인 이후 단계(결제·제작·배송 등)에서는 시안 수정/재승인을 막는다
const FINALIZED_STATUSES: OrderStatus[] = [
  'PROOF_APPROVED',
  'PAYMENT_COMPLETED',
  'PRODUCTION',
  'SHIPPED',
  'COMPLETED',
  'CANCELLED',
];

// 수정요청 항목 문자열('TEXT,COLOR')을 화면용 라벨 목록으로
function revisionItemLabels(value: string | null | undefined): string[] {
  if (!value) return [];
  return value
    .split(',')
    .map((item) => REVISION_ITEM_LABEL[item as RevisionItem])
    .filter(Boolean);
}

function formatDateTime(value: string | null): string {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('ko-KR');
}

interface AccessGateProps {
  orderId: string;
  onVerified: (token: string) => void;
}

function ProofAccessGate({ orderId, onVerified }: AccessGateProps) {
  const [session] = useUserSession();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!session) {
      setIsLoading(false);
      return;
    }

    fetchMyOrderDetail(orderId, session.token)
      .then((order) => {
        if (!order.proofToken) {
          setError('시안 접근 권한이 없습니다.');
          return;
        }
        saveProofToken(orderId, order.proofToken);
        onVerified(order.proofToken);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : '본인 확인 중 오류가 발생했습니다.');
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, session]);

  if (!session) {
    return (
      <section className={styles.gate}>
        <h1>1:1 시안 뷰어</h1>
        <p className={styles.gateDesc}>
          타인의 시안 정보를 보호하기 위해 본인 확인이 필요합니다. 비회원으로 주문하셨다면 주문조회에서 본인 확인 후
          다시 접속해주세요.
        </p>
        <Link to="/lookup" className={styles.actionButton}>
          비회원 주문조회로 이동
        </Link>
        <p className={styles.gateDesc}>회원으로 주문하셨다면 로그인해주세요.</p>
        <Link to="/login" state={{ from: `/mypage/proof/${orderId}` }} className={styles.actionButton}>
          로그인하기
        </Link>
      </section>
    );
  }

  return (
    <section className={styles.gate}>
      <h1>1:1 시안 뷰어</h1>
      {isLoading && <p className={styles.gateDesc}>본인 확인 중...</p>}
      {error && (
        <>
          <p className={styles.errorNotice}>{error}</p>
          <Link to="/mypage" className={styles.actionButton}>
            마이페이지로
          </Link>
        </>
      )}
    </section>
  );
}

function ProofViewer() {
  const { id } = useParams<{ id: string }>();
  const [token, setToken] = useState<string | null>(() => (id ? getProofToken(id) : null));
  const [status, setStatus] = useState<OrderStatus | null>(null);
  const [orderInfo, setOrderInfo] = useState<Pick<ProofData, 'orderNo' | 'customerName' | 'shape' | 'totalPrice'> | null>(
    null,
  );
  const [workStatus, setWorkStatus] = useState<WorkStatus | null>(null);
  const [workCompletedAt, setWorkCompletedAt] = useState<string | null>(null);
  const [proofFile, setProofFile] = useState<ProofFileInfo | null>(null);
  const [feedbacks, setFeedbacks] = useState<ProofFeedbackRecord[]>([]);
  const [revision, setRevision] = useState<ProofRevisionInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [revisionError, setRevisionError] = useState<string | null>(null);
  const [isRevisionOpen, setIsRevisionOpen] = useState(false);
  const [isNoteOpen, setIsNoteOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const reload = () => {
    if (!id || !token) return;
    fetchProof(id, token)
      .then((data) => {
        setOrderInfo({
          orderNo: data.orderNo,
          customerName: data.customerName,
          shape: data.shape,
          totalPrice: data.totalPrice,
        });
        setStatus(data.status);
        setWorkStatus(data.workStatus);
        setWorkCompletedAt(data.workCompletedAt);
        setProofFile(data.proofFile);
        setFeedbacks(data.feedbacks);
        setRevision(data.revision);
      })
      .catch((err) => {
        if (err instanceof ApiError && err.status === 403) {
          clearProofToken(id);
          setToken(null);
          return;
        }
        setLoadError(err instanceof Error ? err.message : '시안 정보를 불러오지 못했습니다.');
      });
  };

  useEffect(reload, [id, token]);

  if (!id) {
    return (
      <section className={styles.gate}>
        <p className={styles.errorNotice}>잘못된 접근입니다.</p>
      </section>
    );
  }

  if (!token) {
    return <ProofAccessGate orderId={id} onVerified={setToken} />;
  }

  if (loadError) {
    return (
      <section className={styles.gate}>
        <p className={styles.errorNotice}>{loadError}</p>
      </section>
    );
  }

  if (status === null) {
    return (
      <section className={styles.gate}>
        <p className={styles.gateDesc}>불러오는 중...</p>
      </section>
    );
  }

  const isFinalized = FINALIZED_STATUSES.includes(status);
  const fileExtension = proofFile?.file_url.split('.').pop()?.toLowerCase();
  const isPaidRevision = revision !== null && revision.used >= revision.freeLimit;
  const remainingRevisions = revision ? Math.max(0, revision.freeLimit - revision.used) : 0;

  // 상담 히스토리에서 고객 수정요청에 회차 번호를 붙인다 (오래된 순으로 센다)
  const revisionRounds = new Map<ProofFeedbackRecord, number>();
  feedbacks
    .slice()
    .reverse()
    .filter((f) => f.sender === 'CUSTOMER' && f.kind === 'REVISION')
    .forEach((f, i) => revisionRounds.set(f, i + 1));

  const handleRevisionSubmit = async (input: ProofRevisionInput): Promise<boolean> => {
    setRevisionError(null);
    try {
      const result = await submitProofRevision(id, input, token);
      setStatus(result.status);
      setIsRevisionOpen(false);
      setNotice(
        result.extraFee > 0
          ? `${result.revisionCount}회차 수정요청(유료 +${result.extraFee.toLocaleString()}원)이 접수되었습니다.`
          : `${result.revisionCount}회차 수정요청이 접수되었습니다. 담당자가 확인 후 수정 시안을 올려드립니다.`,
      );
      reload();
      return true;
    } catch (err) {
      setRevisionError(err instanceof Error ? err.message : '수정요청 처리 중 오류가 발생했습니다.');
      // 다른 창에서 요청해 횟수가 바뀐 경우(402) 최신 횟수로 다시 그려 추가금 동의 체크박스를 보여준다
      if (err instanceof ApiError && err.status === 402) reload();
      return false;
    }
  };

  const handleNoteSubmit = async (input: ProofNoteInput): Promise<boolean> => {
    setActionError(null);
    try {
      await sendProofNote(id, input, token);
      setNotice('쪽지를 보냈습니다. 담당자가 확인 후 답변드립니다.');
      reload();
      return true;
    } catch (err) {
      setActionError(err instanceof Error ? err.message : '쪽지 전송 중 오류가 발생했습니다.');
      return false;
    }
  };

  const handleApprove = async () => {
    setActionError(null);
    try {
      const result = await approveProof(id, token);
      setStatus(result.status);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : '최종 승인 처리 중 오류가 발생했습니다.');
    } finally {
      setIsConfirmOpen(false);
    }
  };

  return (
    <section className={styles.wrapper}>
      {/* 상단: 제목 + 주문 정보 + 상태 배지 */}
      <header className={styles.head}>
        <div className={styles.headText}>
          <span className={styles.eyebrow}>1:1 시안 확인</span>
          <h1>1:1 시안 뷰어</h1>
          <p className={styles.headMeta}>
            주문번호 <strong>{orderInfo?.orderNo ?? id}</strong>
            {orderInfo?.shape && (
              <>
                <span className={styles.dot}>·</span>
                {orderInfo.shape}
              </>
            )}
            {typeof orderInfo?.totalPrice === 'number' && (
              <>
                <span className={styles.dot}>·</span>
                <Link to={`/estimate/${id}`} className={styles.estimateLink}>
                  견적서 다운로드
                </Link>
              </>
            )}
          </p>
        </div>
        <div className={styles.badges}>
          <span className={`${styles.badge} ${isFinalized ? styles.badgeDone : styles.badgeInfo}`}>
            {ORDER_STATUS_LABEL[status]}
          </span>
          {workStatus && (
            <span className={`${styles.badge} ${WORK_BADGE[workStatus]}`}>
              {WORK_STATUS_LABEL[workStatus]}
              {workStatus === 'COMPLETED' && workCompletedAt && ` · ${formatDateTime(workCompletedAt)}`}
            </span>
          )}
        </div>
      </header>

      {/* 시안 */}
      <div className={styles.viewerCard}>
        <div className={styles.cardHead}>
          <h2>시안</h2>
          {proofFile && (
            <a
              href={apiUrl(`/uploads/${proofFile.file_url}`)}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.openLink}
            >
              새 창에서 보기 ↗
            </a>
          )}
        </div>
        <div className={styles.viewer}>
          {!proofFile ? (
            <div className={styles.emptyViewer}>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <rect x="3" y="4" width="18" height="16" rx="2" />
                <circle cx="8.5" cy="9.5" r="1.5" />
                <path d="m21 16-5-5-9 9" />
              </svg>
              <p>아직 등록된 시안이 없습니다.</p>
              <span>담당자가 시안을 올리면 이곳에서 바로 확인할 수 있습니다.</span>
            </div>
          ) : fileExtension === 'pdf' ? (
            <iframe title="시안 PDF" src={apiUrl(`/uploads/${proofFile.file_url}`)} className={styles.frame} loading="lazy" />
          ) : (
            <img
              src={apiUrl(`/uploads/${proofFile.file_url}`)}
              alt="시안"
              className={styles.image}
              loading="lazy"
              decoding="async"
            />
          )}
        </div>
      </div>

      {/* 수정 횟수: 남은 무료 횟수를 크게 노출 */}
      {revision && (
        <div className={`${styles.revisionCard} ${isPaidRevision ? styles.revisionCardPaid : ''}`}>
          <div className={styles.revisionTop}>
            <span className={styles.revisionLabel}>남은 무료 수정</span>
            <span className={styles.revisionCount}>
              <strong>{remainingRevisions}</strong>회 <em>/ 총 {revision.freeLimit}회</em>
            </span>
          </div>
          <ol className={styles.revisionSteps} aria-label={`무료 수정 ${revision.freeLimit}회 중 ${revision.used}회 사용`}>
            {Array.from({ length: revision.freeLimit }, (_, i) => (
              <li key={i} className={i < revision.used ? styles.used : ''}>
                {i + 1}회차
              </li>
            ))}
            <li className={`${styles.paidStep} ${revision.used > revision.freeLimit ? styles.used : ''}`}>
              {revision.freeLimit + 1}회차~ 유료
            </li>
          </ol>
          <p className={styles.revisionPolicy}>
            [수정요청] 양식으로 요청하시면 {revision.freeLimit}회까지 무료로 수정해드리며, 이후 추가 수정은 1회당{' '}
            {revision.extraFee.toLocaleString()}원이 결제금액에 추가됩니다. 단순 문의는 횟수가 차감되지 않는 쪽지를
            이용해주세요.
          </p>
          {isPaidRevision && !isFinalized && (
            <p className={styles.revisionWarning}>
              무료 수정 횟수를 모두 사용했습니다. 추가 수정 요청 시 1회당 {revision.extraFee.toLocaleString()}원이
              결제금액에 추가됩니다.
            </p>
          )}
          {revision.totalExtraFee > 0 && (
            <p className={styles.revisionExtra}>
              누적 추가 수정 비용 <strong>{revision.totalExtraFee.toLocaleString()}원</strong> (결제금액에 포함)
            </p>
          )}
        </div>
      )}

      {notice && <p className={styles.infoNotice}>{notice}</p>}
      {actionError && <p className={styles.errorNotice}>{actionError}</p>}

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.actionButton}
          aria-expanded={isNoteOpen}
          onClick={() => setIsNoteOpen((prev) => !prev)}
        >
          쪽지 보내기
        </button>
        {!isFinalized && (
          <>
            <button
              type="button"
              className={`${styles.actionButton} ${styles.revisionButton}`}
              onClick={() => {
                setRevisionError(null);
                setIsRevisionOpen(true);
              }}
            >
              {isPaidRevision ? '수정요청 (유료)' : `수정요청 (${remainingRevisions}회 남음)`}
            </button>
            <button
              type="button"
              className={`${styles.actionButton} ${styles.approveButton}`}
              onClick={() => setIsConfirmOpen(true)}
            >
              최종 승인
            </button>
          </>
        )}
      </div>

      {isNoteOpen && <ProofNoteForm onSubmit={handleNoteSubmit} />}

      {isFinalized && <p className={styles.finalNotice}>최종 승인이 완료되어 더 이상 수정/취소할 수 없습니다.</p>}

      {status === 'PROOF_APPROVED' && orderInfo && orderInfo.totalPrice == null && (
        <p className={styles.infoNotice}>
          결제 금액을 산정하고 있습니다. 담당자가 금액을 확정하면 이 화면에서 결제하실 수 있습니다.
        </p>
      )}

      {status === 'PROOF_APPROVED' && orderInfo && typeof orderInfo.totalPrice === 'number' && (
        <PaymentPanel
          orderNo={orderInfo.orderNo}
          orderName={orderInfo.shape ? `${orderInfo.shape} 제작` : '주문 결제'}
          amount={orderInfo.totalPrice}
          customerName={orderInfo.customerName}
        />
      )}


      {/* 상담 내역: 담당자는 왼쪽, 나는 오른쪽 말풍선 */}
      {feedbacks.length > 0 && (
        <div className={styles.history}>
          <h2 className={styles.historyTitle}>1:1 상담 히스토리</h2>
          <ul className={styles.historyList}>
            {feedbacks
              .slice()
              .reverse()
              .map((feedback, index) => {
                const isAdmin = feedback.sender === 'ADMIN';
                const round = revisionRounds.get(feedback);
                const items = revisionItemLabels(feedback.revision_items);
                return (
                  <li key={index} className={`${styles.historyItem} ${isAdmin ? styles.fromAdmin : styles.fromMe}`}>
                    <span className={styles.avatar}>{isAdmin ? '담' : '나'}</span>
                    <div className={styles.bubbleWrap}>
                      <div className={styles.bubbleMeta}>
                        <span className={styles.historySender}>{isAdmin ? '담당자' : '나'}</span>
                        {round ? (
                          <span className={`${styles.kindTag} ${styles.kindRevision}`}>
                            수정요청 {round}회차{feedback.extra_fee ? ' · 유료' : ''}
                          </span>
                        ) : (
                          !isAdmin && <span className={styles.kindTag}>쪽지</span>
                        )}
                        <span className={styles.historyTime}>{formatDateTime(feedback.created_at)}</span>
                      </div>
                      <div className={`${styles.bubble} ${round ? styles.bubbleRevision : ''}`}>
                        {items.length > 0 && (
                          <div className={styles.itemChips}>
                            {items.map((label) => (
                              <span key={label}>{label}</span>
                            ))}
                          </div>
                        )}
                        <p className={styles.historyMessage}>{feedback.message}</p>
                        {feedback.attachment_path && (
                          <a
                            href={apiUrl(`/uploads/${feedback.attachment_path}`)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.historyAttachment}
                          >
                            <img src={apiUrl(`/uploads/${feedback.attachment_path}`)} alt="첨부 이미지" loading="lazy" />
                          </a>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
          </ul>
        </div>
      )}

      {revision && (
        <ProofRevisionModal
          isOpen={isRevisionOpen}
          revision={revision}
          error={revisionError}
          onClose={() => setIsRevisionOpen(false)}
          onSubmit={handleRevisionSubmit}
        />
      )}

      <ConfirmModal
        isOpen={isConfirmOpen}
        title="최종 승인"
        description="최종 승인 후 수정/취소가 불가능합니다. 계속하시겠습니까?"
        confirmLabel="최종 승인"
        cancelLabel="취소"
        onConfirm={handleApprove}
        onCancel={() => setIsConfirmOpen(false)}
      />
    </section>
  );
}

export default ProofViewer;
